import { Bone, BoxGeometry, Color, Float32BufferAttribute, Group, InstancedMesh, Mesh, Skeleton, SkinnedMesh, Uint16BufferAttribute, type BufferGeometry, type Camera, type Material, type Object3D, type Scene, type ShaderMaterial, type WebGLRenderer, type WebGLRenderTarget } from "three";
import type { AtmosphereUniforms } from "./atmosphere.ts";
import { PALETTE } from "@eldrun/content";
import { SKILL_SHAPES } from "./geometry/skill-shapes.ts";
import { createGhostMaterial } from "./shaders/ghost.ts";
import { createSkillMeshMaterial } from "./shaders/skill-mesh.ts";
import { SURFACE_VERTEX } from "./shaders/surface.ts";
import { avatarMaterial } from "./shaders/avatar.ts";
import { clampStyle } from "./character-surface/config.ts";
import { characterBaseMaterial, createEnergyMaterial, energyUniforms, energyVariant } from "./character-surface/material.ts";
import { prepareEnergyGeometry } from "./character-surface/prepare-geometry.ts";
import { toonDissolveMaterial, toonMaterial } from "./shaders/toon.ts";

/**
 * O que decide o programa de um objeto, além da fonte e das defines do material: lote, cor por
 * instância, skinning e a presença do atributo `normal` (o three r186 põe `HAS_NORMAL` na chave; também a
 * posição e a cor com alfa, marcadas só quando fogem do comum). As malhas do kit e dos modelos não têm
 * normal (a luz usa a normal por face); as primitivas do three (bonecos, criaturas, o cubo dos drops) têm.
 */
export function programSignature(object: Object3D): string | undefined {
  const mesh = object as Mesh;
  if (!mesh.isMesh) return undefined;
  const material = mesh.material as ShaderMaterial;
  const source = material.vertexShader === SURFACE_VERTEX ? "superficie" : material.name;
  const instanced = (mesh as InstancedMesh).isInstancedMesh === true;
  const color = instanced && (mesh as InstancedMesh).instanceColor !== null;
  const skin = (mesh as SkinnedMesh).isSkinnedMesh === true;
  const normal = mesh.geometry.getAttribute("normal") !== undefined;
  // F95: a chave também leva a posição e a cor com alfa (`vertexAlphas`); a malha sem posição (uma vaga
  // parada, que a pré-compilação compila mesmo invisível) pedia outra variante.
  const position = mesh.geometry.getAttribute("position") === undefined ? "|sem-posicao" : "";
  const alpha = material.vertexColors && mesh.geometry.getAttribute("color")?.itemSize === 4 ? "|cor-alfa" : "";
  return `${source}|${Object.keys(material.defines ?? {}).sort().join(",")}|cor=${material.vertexColors}|lote=${instanced}|corInst=${color}|pele=${skin}|normal=${normal}${position}${alpha}`;
}

/** Uma cópia sem normal nem uv: o formato das malhas do kit e dos modelos. */
function bare(geometry: BufferGeometry): BufferGeometry {
  const out = geometry.clone();
  out.deleteAttribute("normal");
  out.deleteAttribute("uv");
  return out;
}

/**
 * Coluna com dois ossos (base e meio), de `height` m: a metade de cima segue o osso do meio (peso 1),
 * a de baixo a base, e a junta divide. O `SkinnedMesh` mínimo do material de superfície (F51), sem
 * morphs: o manequim da vitrine das criaturas e a variante com skinning da pré-compilação (com o
 * material dos avatares, F49, o mesmo programa deles: sem normal, cor por vértice e skinning).
 */
export function skinnedColumn(material: Material, height = 2, width = 0.3): { mesh: SkinnedMesh; joint: Bone } {
  // Sem normal, como sairão os modelos animados pelo pipeline (a luz usa a normal por face).
  const geometry = bare(new BoxGeometry(width, height, width, 1, 4, 1));
  geometry.translate(0, height / 2, 0);
  const position = geometry.getAttribute("position");
  const index: number[] = [];
  const weight: number[] = [];
  for (let i = 0; i < position.count; i++) {
    const t = position.getY(i) / height;
    const upper = t > 0.5 + 1e-6 ? 1 : t < 0.5 - 1e-6 ? 0 : 0.5;
    index.push(0, 1, 0, 0);
    weight.push(1 - upper, upper, 0, 0);
  }
  geometry.setAttribute("skinIndex", new Uint16BufferAttribute(index, 4));
  geometry.setAttribute("skinWeight", new Float32BufferAttribute(weight, 4));
  const root = new Bone();
  const joint = new Bone();
  joint.position.y = height / 2;
  root.add(joint);
  const mesh = new SkinnedMesh(geometry, material);
  mesh.add(root);
  mesh.bind(new Skeleton([root, joint]));
  mesh.frustumCulled = false;
  return { mesh, joint };
}

/**
 * Pré-compilação dos programas (F51): a cena e, num grupo fora dela, uma malha mínima de cada
 * variante que só aparece depois (o fantasma de construção, as criaturas em lote, o item na mão, as
 * estruturas e os drops; F49/F50: o corpo com skinning e cor por vértice dos avatares e das criaturas
 * animadas e o dissolve dele, e, sem o pacote dos avatares, o dissolve dos bonecos procedurais), com o
 * mesmo formato de geometria dos
 * objetos de verdade (`programSignature`), por `compileAsync` (em paralelo onde há
 * `KHR_parallel_shader_compile`). O grupo fica vivo com a cena: liberar os materiais dele liberaria os
 * programas, que o cache divide com os de verdade.
 */
export class ProgramPrewarm {
  private readonly group = new Group();
  private readonly materials: Material[] = [];

  constructor(atmosphere: AtmosphereUniforms, foliage: Material, skinnedAvatars = true) {
    const toon = toonMaterial(atmosphere, "#808080", 1, false, { outline: true });
    const avatar = avatarMaterial(atmosphere);
    // Quem morre some em dissolve: o corpo com skin (avatares, F49; criaturas animadas, F50, que
    // carregam no meio da partida com ou sem o pacote dos avatares) e, na reserva, as peças do boneco
    // procedural.
    const dissolve = toonDissolveMaterial(avatar);
    const procedural = skinnedAvatars ? undefined : toonDissolveMaterial(toon);
    // Superfície dos atores (F119, ADR 0025): o PBR com skin e cor por vértice (avatares e criaturas
    // animados) e o dissolve dele, e o de cor sólida em lote (bonecos das criaturas): +3 programas. Só
    // na reserva sem o pacote dos avatares, o boneco procedural dos heróis (fora de lote) e o dissolve dele.
    const style = clampStyle();
    const actorSkinned = createEnergyMaterial(characterBaseMaterial({ vertexColors: true }), atmosphere, energyUniforms(style));
    const actorSkinnedDissolve = energyVariant(actorSkinned, { defines: { USE_DISSOLVE: "" } });
    const actorSolid = createEnergyMaterial(characterBaseMaterial(), atmosphere, energyUniforms(style));
    const actorSolidDissolve = skinnedAvatars ? undefined : energyVariant(actorSolid, { defines: { USE_DISSOLVE: "" } });
    this.materials.push(actorSkinned, actorSkinnedDissolve, actorSolid, ...(actorSolidDissolve ? [actorSolidDissolve] : []));
    const ghost = createGhostMaterial(atmosphere);
    // Peças 3D das habilidades (F78): um programa para todas.
    const piece = createSkillMeshMaterial(PALETTE.neutro.hex);
    this.materials.push(dissolve, ghost, piece, ...(procedural ? [procedural] : []));
    // Primitivas do three (com normal): bonecos, criaturas e o cubo dos drops. Kit e modelos: sem.
    const box = new BoxGeometry(0.1, 0.1, 0.1);
    const kit = bare(box);
    const instanced = (geometry: BufferGeometry, material: Material, color: boolean): InstancedMesh => {
      const mesh = new InstancedMesh(geometry, material, 1);
      if (color) mesh.setColorAt(0, new Color(1, 1, 1));
      return mesh;
    };
    // O `DissolvePool` (F95) desenha no programa do dissolve do avatar de superfície; na reserva
    // procedural, as peças do toon que ainda somem.
    const bodies = [skinnedColumn(avatar).mesh, skinnedColumn(dissolve).mesh, ...(procedural ? [new Mesh(box, procedural)] : [])];
    // Os atores na superfície (com os atributos dela, como os de verdade).
    const actorBox = prepareEnergyGeometry(box);
    const actors = [skinnedColumn(actorSkinned), skinnedColumn(actorSkinnedDissolve)].map(({ mesh }) => {
      mesh.geometry = prepareEnergyGeometry(mesh.geometry);
      return mesh;
    });
    this.group.add(
      new Mesh(box, toon), // bonecos procedurais (e a jangada, as flechas, o caldeirão)
      instanced(box, toon, false), // peças acesas das criaturas em lote
      ...bodies,
      ...actors, // avatares e criaturas animados (e o dissolve deles)
      instanced(actorBox, actorSolid, false), // bonecos das criaturas em lote
      ...(skinnedAvatars ? [] : [new Mesh(actorBox, actorSolid)]), // na reserva, o boneco procedural dos heróis (fora de lote)
      ...(actorSolidDissolve ? [new Mesh(actorBox, actorSolidDissolve)] : []), // na reserva, o boneco que some
      new Mesh(kit, ghost), // fantasma de construção
      new Mesh(SKILL_SHAPES.no.clone(), piece), // peças das habilidades (com `aBary`; cópia: o descarte não toca a geometria dividida)
      new Mesh(kit, foliage), // item na mão
      instanced(kit, foliage, false), // estruturas e drops com modelo, em lote
      instanced(box, foliage, true), // cubos dos drops sem modelo
    );
  }

  /** As assinaturas de programa que a pré-compilação cobre. */
  signatures(): Set<string> {
    const out = new Set<string>();
    this.group.traverse((o) => {
      const s = programSignature(o);
      if (s) out.add(s);
    });
    return out;
  }

  /**
   * Compila a cena e as variantes; resolve quando estão prontas. `target`: o alvo em que a cena será
   * desenhada (F52: o do compositor). O three põe na chave do programa a saída de cor do alvo atual
   * (sRGB no canvas, linear num alvo): compilar com o canvas preparava programas que o compositor
   * não usa. O `compileAsync` escolhe os programas na hora da chamada.
   */
  run(renderer: WebGLRenderer, scene: Scene, camera: Camera, target: WebGLRenderTarget | null = null): Promise<unknown> {
    const previous = renderer.getRenderTarget();
    renderer.setRenderTarget(target);
    try {
      return Promise.all([renderer.compileAsync(scene, camera), renderer.compileAsync(this.group, camera, scene)]);
    } finally {
      renderer.setRenderTarget(previous);
    }
  }

  dispose(): void {
    this.group.traverse((o: Object3D) => {
      (o as Mesh).geometry?.dispose();
      (o as SkinnedMesh).skeleton?.dispose();
    });
    for (const m of this.materials) m.dispose();
  }
}
